import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDataHubClient } from '../../context/DataHubClientContext.js';
import type { DataEntry } from '../../services/dataHubService.interface.js';
import { DataHubService } from '../../services/dataHubService.js';

export interface UseDataEntryDetailParams {
  dataSetId: string;
  dataId: string;
  dataSetVersion: number;
  enabled: boolean;
}

export interface UseDataEntryDetailResult {
  entry: DataEntry | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

// Session-scoped caches — survive component unmount/remount within the same page session.
// `pendingFetches` deduplicates concurrent requests for the same key (e.g. React Strict Mode
// double-invoking effects). `detailCache` avoids repeat network calls for already-fetched entries.
const detailCache = new Map<string, DataEntry>();
const pendingFetches = new Map<string, Promise<DataEntry>>();

export function useDataEntryDetail({
  dataSetId,
  dataId,
  dataSetVersion,
  enabled,
}: UseDataEntryDetailParams): UseDataEntryDetailResult {
  const client = useDataHubClient();
  const service = useMemo(() => new DataHubService(client), [client]);

  const cacheKey = `${dataSetId}:${dataId}:${dataSetVersion}`;

  const [entry, setEntry] = useState<DataEntry | null>(
    () => detailCache.get(cacheKey) ?? null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchTick, setFetchTick] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    const cached = detailCache.get(cacheKey);
    if (cached) {
      setEntry(cached);
      return;
    }

    // Reuse an in-flight request for the same key instead of issuing a second one.
    let pending = pendingFetches.get(cacheKey);
    if (!pending) {
      pending = service
        .getDataEntry(dataSetId, dataId, dataSetVersion)
        .then((result) => {
          detailCache.set(cacheKey, result);
          pendingFetches.delete(cacheKey);
          return result;
        })
        .catch((err: unknown) => {
          pendingFetches.delete(cacheKey);
          throw err;
        });
      pendingFetches.set(cacheKey, pending);
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    pending
      .then((result) => {
        if (!cancelled) {
          setEntry(result);
          setIsLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to load entry detail',
          );
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    service,
    dataSetId,
    dataId,
    dataSetVersion,
    enabled,
    fetchTick,
    cacheKey,
  ]);

  const refetch = useCallback(() => {
    detailCache.delete(cacheKey);
    pendingFetches.delete(cacheKey);
    setFetchTick((t) => t + 1);
  }, [cacheKey]);

  return { entry, isLoading, error, refetch };
}
